import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { Pencil, Save, Lock, Download, Upload, Loader2, ListChecks, CheckCircle2, AlertTriangle, ChevronDown, ChevronRight, ArrowRight, Divide } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useOrgId } from '../../hooks/useOrgId';
import { useAcademicYear } from '../../context/AcademicYearContext';
import { useT } from '../../i18n/LanguageContext';
import { schoolApi } from '../../api/school';
import { PageHeader, EmptyState, InlineSpinner, Card } from '../../components/school/ui';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Checkbox } from '../../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Progress } from '../../components/ui/progress';
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter, AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel } from '../../components/ui/alert-dialog';
import { toast } from 'sonner';

// Temporarily hide the "Divide grades by 2" tool in the Missing Grades dialog.
// The backend /marks/scale endpoint and handler remain intact — flip this to
// true to re-enable the button.
const SHOW_DIVIDE_TOOLS = false;

export default function MarksEntryPage() {
  const { user } = useAuth();
  const { t } = useT();
  const orgId = useOrgId();
  const { selectedYearId } = useAcademicYear();
  const [exams, setExams] = useState([]);
  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [examId, setExamId] = useState('');
  const [classId, setClassId] = useState('');
  const [sectionId, setSectionId] = useState('');
  const [subjectId, setSubjectId] = useState('');
  const [rows, setRows] = useState([]);
  const [subject, setSubject] = useState(null);
  const [examLocked, setExamLocked] = useState(false);
  const [loadingGrid, setLoadingGrid] = useState(false);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [missOpen, setMissOpen] = useState(false);
  const [missLoading, setMissLoading] = useState(false);
  const [missData, setMissData] = useState(null);
  const [missExpanded, setMissExpanded] = useState({});
  const [scaleTarget, setScaleTarget] = useState(null);
  const [scaling, setScaling] = useState(false);
  const fileRef = useRef(null);

  const init = useCallback(async () => {
    if (!orgId) return;
    const [ex, cls] = await Promise.all([
      schoolApi.listExams(orgId, { academic_year_id: selectedYearId }),
      schoolApi.listClasses(orgId, selectedYearId),
    ]);
    setExams(ex); setClasses(cls);
    // Clear selections that may belong to a different year
    setExamId(''); setClassId(''); setSectionId(''); setSubjectId(''); setRows([]);
  }, [orgId, selectedYearId]);
  useEffect(() => { init(); }, [init]);

  useEffect(() => { if (classId) { schoolApi.listSections(orgId, classId, selectedYearId).then(setSections); schoolApi.listSubjects(orgId, classId, undefined, selectedYearId).then(setSubjects); } else { setSections([]); setSubjects([]); } setSectionId(''); setSubjectId(''); }, [orgId, classId, selectedYearId]);

  // Reflect exam lock state immediately when an exam is selected
  useEffect(() => {
    const ex = exams.find((e) => e.id === examId);
    setExamLocked(!!ex?.is_locked);
  }, [examId, exams]);

  const loadGrid = useCallback(async () => {
    if (!examId || !classId || !sectionId || !subjectId) { setRows([]); return; }
    setLoadingGrid(true);
    try {
      const subj = subjects.find((s) => s.id === subjectId);
      setSubject(subj);
      const ex = exams.find((e) => e.id === examId);
      setExamLocked(!!ex?.is_locked);
      const [students, marks] = await Promise.all([
        schoolApi.listStudents(orgId, { class_id: classId, section_id: sectionId, academic_year_id: selectedYearId, limit: 10000 }),
        schoolApi.getMarks({ organization_id: orgId, exam_id: examId, subject_id: subjectId, section_id: sectionId }),
      ]);
      const studentList = students.students || [];
      const markMap = {};
      marks.forEach((m) => { markMap[m.student_id] = m; });
      setRows(studentList.map((s) => ({
        student_id: s.id, name: s.name, roll_no: s.roll_no, admission_no: s.admission_no,
        cc: markMap[s.id]?.cc ?? '',
        comp: markMap[s.id]?.comp ?? '',
        cc_absent: !!(markMap[s.id]?.cc_absent || markMap[s.id]?.is_absent),
        comp_absent: !!(markMap[s.id]?.comp_absent || markMap[s.id]?.is_absent),
      })));
    } catch (e) { toast.error('Failed to load grid'); }
    finally { setLoadingGrid(false); }
  }, [examId, classId, sectionId, subjectId, orgId, subjects, exams, selectedYearId]);

  useEffect(() => { loadGrid(); }, [loadGrid]);

  // Subjects are loaded by class, but a class can have the same subject assigned across
  // multiple sections (e.g. "Français" in section A and B). Once a section is chosen,
  // show only that section's subjects (plus any class-wide subject with no section),
  // de-duplicated by name, so the dropdown isn't cluttered with cross-section copies.
  const sectionSubjects = useMemo(() => {
    if (!sectionId) return subjects;
    const inSection = subjects.filter((s) => s.section_id === sectionId);
    const classWide = subjects.filter((s) => !s.section_id);
    const merged = [...inSection, ...classWide];
    const seen = new Set();
    return merged.filter((s) => {
      const key = (s.name || '').trim().toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [subjects, sectionId]);

  // Reset the selected subject whenever the section changes (it may not exist in the new list)
  useEffect(() => { setSubjectId(''); }, [sectionId]);

  const setRow = (i, key, val) => setRows((r) => { const n = [...r]; n[i] = { ...n[i], [key]: val }; return n; });

  const maxNote = subject?.max_note || subject?.full_marks || 20;
  // Term note = average over both components; an absent component counts as 0,
  // an empty (not-absent) component is excluded. Both absent -> AB (null).
  const computeNote = (r) => {
    if (r.cc_absent && r.comp_absent) return null;
    const parts = [];
    if (r.cc_absent) parts.push(0);
    else if (r.cc !== '' && r.cc !== null && r.cc !== undefined) parts.push(Number(r.cc));
    if (r.comp_absent) parts.push(0);
    else if (r.comp !== '' && r.comp !== null && r.comp !== undefined) parts.push(Number(r.comp));
    if (!parts.length) return null;
    return Math.round((parts.reduce((a, b) => a + b, 0) / parts.length) * 100) / 100;
  };

  // A component value is out of range when it is below 0 or above the subject's max note.
  const isOutOfRange = (val) => {
    if (val === '' || val === null || val === undefined) return false;
    const n = Number(val);
    return Number.isNaN(n) ? false : (n > maxNote || n < 0);
  };
  const ccInvalid = (r) => !r.cc_absent && isOutOfRange(r.cc);
  const compInvalid = (r) => !r.comp_absent && isOutOfRange(r.comp);
  const invalidCount = useMemo(
    () => rows.reduce((acc, r) => acc + (ccInvalid(r) ? 1 : 0) + (compInvalid(r) ? 1 : 0), 0),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows, maxNote]
  );

  const save = async () => {
    if (!subject) return;
    if (invalidCount > 0) {
      toast.error(`${t('marks.fixInvalid', 'Fix grades outside the allowed range before saving')} (0–${maxNote}).`);
      return;
    }
    setSaving(true);
    try {
      await schoolApi.saveMarks({
        organization_id: orgId, exam_id: examId, subject_id: subjectId, class_id: classId, section_id: sectionId,
        full_marks: maxNote, pass_marks: subject.pass_marks || maxNote / 2, max_note: maxNote,
        entries: rows.map((r) => ({
          student_id: r.student_id,
          cc: r.cc_absent || r.cc === '' ? null : Number(r.cc),
          comp: r.comp_absent || r.comp === '' ? null : Number(r.comp),
          cc_absent: !!r.cc_absent,
          comp_absent: !!r.comp_absent,
          is_absent: !!(r.cc_absent && r.comp_absent),
        })),
      });
      toast.success('Marks saved');
      loadGrid();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to save marks'); }
    finally { setSaving(false); }
  };

  const filtersReady = examId && classId && sectionId && subjectId;

  const loadMissing = useCallback(async () => {
    if (!examId || !classId) { setMissData(null); return; }
    setMissLoading(true);
    setMissExpanded({});
    try {
      const params = { organization_id: orgId, exam_id: examId, class_id: classId };
      if (sectionId) params.section_id = sectionId;
      const data = await schoolApi.marksCompletion(params);
      setMissData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('marks.missingFailed', 'Failed to load missing grades'));
    } finally { setMissLoading(false); }
  }, [orgId, examId, classId, sectionId, t]);

  // Re-fetch whenever the dialog is open and the exam/class/section changes
  // (e.g. switching the exam via the dialog's own selector).
  useEffect(() => {
    if (missOpen) loadMissing();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [missOpen, examId, classId, sectionId]);

  const openMissing = () => {
    if (!classId) return;
    setMissData(null);
    setMissExpanded({});
    setMissOpen(true);
  };

  const fillFromMissing = (secId, subjId) => {
    setSectionId(secId);
    // subject is reset by the section-change effect, so set it on the next tick
    setTimeout(() => setSubjectId(subjId), 0);
    setMissOpen(false);
  };

  // Divide every C.C and Comp grade for a (section, subject) by 2 — used to fix
  // grades a teacher entered on the wrong (doubled) scale.
  const doScale = async () => {
    if (!scaleTarget) return;
    setScaling(true);
    try {
      const res = await schoolApi.scaleMarks({
        organization_id: orgId, exam_id: examId, class_id: classId,
        section_id: scaleTarget.section_id, subject_id: scaleTarget.subject_id, factor: 2,
      });
      toast.success(`${t('marks.dividedDone', 'Divided')} ${res.count} ${t('marks.gradeRecords', 'grade record(s)')} ÷2`);
      setScaleTarget(null);
      await loadMissing();
      if (subjectId === scaleTarget.subject_id && sectionId === scaleTarget.section_id) loadGrid();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('marks.divideFailed', 'Failed to divide grades'));
    } finally { setScaling(false); }
  };

  const toggleMissRow = (key) => setMissExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  const handleExport = async () => {
    if (!examId || !classId || !sectionId) return;
    setExporting(true);
    try {
      // One workbook, one sheet per subject (all subjects of this class+section)
      const blob = await schoolApi.exportMarksWorkbook({
        organization_id: orgId, exam_id: examId, class_id: classId, section_id: sectionId,
      });
      const url = window.URL.createObjectURL(blob);
      const cname = classes.find((c) => c.id === classId)?.name || 'class';
      const sname = sections.find((s) => s.id === sectionId)?.name || 'sec';
      const a = document.createElement('a');
      a.href = url;
      a.download = `marks_${cname}_${sname}_all-subjects.xlsx`.replace(/[^A-Za-z0-9_.-]+/g, '-');
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(t('marks.exportFailed', 'Failed to export Excel')); }
    finally { setExporting(false); }
  };

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setImporting(true);
    try {
      // Multi-sheet aware import (each sheet -> its subject)
      const r = await schoolApi.importMarksWorkbook(
        { organization_id: orgId, exam_id: examId, class_id: classId, section_id: sectionId },
        file,
      );
      toast.success(r.message || t('common.saved'));
      const firstErr = r.results?.find((x) => x.errors?.length)?.errors?.[0] || r.results?.find((x) => x.error)?.error;
      if (r.skipped > 0 && firstErr) toast.warning(`${r.skipped} ${t('marks.rowsSkipped', 'row(s) skipped')}: ${firstErr}`);
      loadGrid();
    } catch (err) { toast.error(err?.response?.data?.detail || t('marks.importFailed', 'Failed to import Excel')); }
    finally { setImporting(false); }
  };

  return (
    <div className="max-w-[1400px] mx-auto">
      <PageHeader title={t('nav.marks')} subtitle="Enter examination marks" icon={Pencil} />

      <Card className="p-4 mb-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <Select value={examId} onValueChange={setExamId}>
            <SelectTrigger data-testid="marks-exam-select"><SelectValue placeholder={t('nav.exams')} /></SelectTrigger>
            <SelectContent>{exams.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={classId} onValueChange={setClassId}>
            <SelectTrigger data-testid="marks-class-select"><SelectValue placeholder={t('students.class')} /></SelectTrigger>
            <SelectContent>{classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={sectionId} onValueChange={setSectionId} disabled={!classId}>
            <SelectTrigger data-testid="marks-section-select"><SelectValue placeholder={t('students.section')} /></SelectTrigger>
            <SelectContent>{sections.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={subjectId} onValueChange={setSubjectId} disabled={!sectionId}>
            <SelectTrigger data-testid="marks-subject-select"><SelectValue placeholder={t('rc.subject')} /></SelectTrigger>
            <SelectContent>{sectionSubjects.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="mt-3 flex justify-end">
          <Button variant="outline" onClick={openMissing} disabled={!classId} className="gap-2" data-testid="marks-missing-button"
            title={t('marks.missingHint', 'See which grades are still missing for this class & term')}>
            <ListChecks className="w-4 h-4" /> {t('marks.missingGrades', 'Missing Grades')}
          </Button>
        </div>
      </Card>

      {examLocked && examId && (
        <div className="mb-4 flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="marks-locked-banner">
          <Lock className="w-4 h-4 flex-shrink-0" />
          This exam is <strong>locked</strong>. Marks are read-only. Unlock it from the Exams page to make changes.
        </div>
      )}

      <Card>
        {loadingGrid ? <InlineSpinner /> : !subjectId ? (
          <EmptyState icon={Pencil} title="Select exam, class, section & subject" description="Choose the filters above to enter marks" />
        ) : rows.length === 0 ? (
          <EmptyState icon={Pencil} title={t('common.noData')} description="No students in this section" />
        ) : (
          <>
            <div className="flex items-center justify-between px-4 py-3 border-b border-border">
              <div className="text-sm text-muted-foreground">{subject?.name} &middot; {t('subjects.maxNote', 'Max note')}: <strong>{maxNote}</strong>{subject?.uv_group ? <> &middot; {subject.uv_group}</> : null}</div>
              <div className="flex items-center gap-2">
                <Button variant="outline" onClick={handleExport} disabled={exporting} className="gap-2" data-testid="marks-export-button" title={t('marks.downloadAllHint', 'Downloads one file with a sheet per subject (incl. teachers)')}>
                  {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} {t('marks.downloadAll', 'Download Excel (all subjects)')}
                </Button>
                {!examLocked && (
                  <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={importing} className="gap-2" data-testid="marks-import-button" title={t('marks.importAllHint', 'Imports every sheet (all subjects) back')}>
                    {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} {t('marks.importExcel', 'Import Excel')}
                  </Button>
                )}
                {examLocked ? <Badge variant="outline" className="text-amber-600 border-amber-200 bg-amber-50"><Lock className="w-3 h-3 mr-1" /> Exam Locked</Badge> :
                  <Button onClick={save} disabled={saving || invalidCount > 0} className="gap-2" data-testid="marks-save-button"><Save className="w-4 h-4" /> {saving ? t('common.saving') : t('common.save')}</Button>}
                <input ref={fileRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={handleImportFile} data-testid="marks-import-input" />
              </div>
            </div>
            {invalidCount > 0 && (
              <div className="flex items-center gap-2 mx-4 mt-3 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive" data-testid="marks-invalid-banner">
                <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                <span><strong className="tabular-nums">{invalidCount}</strong> {t('marks.invalidGrades', 'grade(s) are outside the allowed range')} (0–{maxNote}). {t('marks.invalidGradesHint', 'Correct them before saving.')}</span>
              </div>
            )}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                  <th className="px-4 py-3">{t('students.rollNo')}</th>
                  <th className="px-4 py-3">{t('common.name')}</th>
                  <th className="px-4 py-3 w-28">C.C</th>
                  <th className="px-4 py-3 w-16 text-center">{t('marks.ccAbsent', 'C.C Abs')}</th>
                  <th className="px-4 py-3 w-28">COMP</th>
                  <th className="px-4 py-3 w-16 text-center">{t('marks.compAbsent', 'Comp Abs')}</th>
                  <th className="px-4 py-3 w-24 text-right">{t('subjects.maxNote', 'Note')}</th>
                </tr></thead>
                <tbody>
                  {rows.map((r, i) => {
                    const note = computeNote(r);
                    const fullyAbsent = r.cc_absent && r.comp_absent;
                    return (
                    <tr key={r.student_id} className="border-b border-border last:border-0" data-testid={`marks-row-${r.student_id}`}>
                      <td className="px-4 py-2 tabular-nums">{r.roll_no || 'N/A'}</td>
                      <td className="px-4 py-2 font-medium">{r.name}</td>
                      <td className="px-4 py-2">
                        <Input type="number" className={`h-9 w-24 ${ccInvalid(r) ? 'border-destructive focus-visible:ring-destructive text-destructive' : ''}`} value={r.cc_absent ? '' : r.cc} disabled={r.cc_absent || examLocked} max={maxNote} min="0"
                          onChange={(e) => setRow(i, 'cc', e.target.value)} data-testid={`marks-cc-${r.student_id}`} />
                        {ccInvalid(r) && <p className="text-[10px] text-destructive mt-0.5" data-testid={`marks-cc-invalid-${r.student_id}`}>{t('marks.allowedRange', 'Allowed')}: 0–{maxNote}</p>}
                      </td>
                      <td className="px-4 py-2 text-center">
                        <Checkbox checked={r.cc_absent} disabled={examLocked} onCheckedChange={(v) => setRow(i, 'cc_absent', !!v)} data-testid={`marks-cc-absent-${r.student_id}`} />
                      </td>
                      <td className="px-4 py-2">
                        <Input type="number" className={`h-9 w-24 ${compInvalid(r) ? 'border-destructive focus-visible:ring-destructive text-destructive' : ''}`} value={r.comp_absent ? '' : r.comp} disabled={r.comp_absent || examLocked} max={maxNote} min="0"
                          onChange={(e) => setRow(i, 'comp', e.target.value)} data-testid={`marks-comp-${r.student_id}`} />
                        {compInvalid(r) && <p className="text-[10px] text-destructive mt-0.5" data-testid={`marks-comp-invalid-${r.student_id}`}>{t('marks.allowedRange', 'Allowed')}: 0–{maxNote}</p>}
                      </td>
                      <td className="px-4 py-2 text-center">
                        <Checkbox checked={r.comp_absent} disabled={examLocked} onCheckedChange={(v) => setRow(i, 'comp_absent', !!v)} data-testid={`marks-comp-absent-${r.student_id}`} />
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums font-semibold" data-testid={`marks-note-${r.student_id}`}>{fullyAbsent ? 'AB' : (note != null ? `${note}/${maxNote}` : '-')}</td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Card>

      {/* Missing grades dialog */}
      <Dialog open={missOpen} onOpenChange={setMissOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="marks-missing-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ListChecks className="w-5 h-5" />
              {t('marks.missingGrades', 'Missing Grades')}
              <span className="text-sm font-normal text-muted-foreground">— {missData?.class_name || classes.find((c) => c.id === classId)?.name}</span>
            </DialogTitle>
          </DialogHeader>

          {/* Exam (term) filter inside the dialog */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 pb-1">
            <span className="text-sm font-medium text-muted-foreground">{t('nav.exams', 'Exam')}</span>
            <Select value={examId} onValueChange={setExamId}>
              <SelectTrigger className="sm:w-[260px]" data-testid="marks-missing-exam-select">
                <SelectValue placeholder={t('marks.selectExam', 'Select an exam')} />
              </SelectTrigger>
              <SelectContent>{exams.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          {!examId ? (
            <EmptyState icon={ListChecks} title={t('marks.selectExam', 'Select an exam')} description={t('marks.selectExamHint', 'Choose an exam above to see which grades are missing.')} />
          ) : missLoading ? (
            <InlineSpinner />
          ) : !missData ? null : (
            <div className="space-y-4">
              {/* Summary */}
              <div className="rounded-lg border border-border p-4">
                <div className="flex items-center justify-between mb-2 text-sm">
                  <span className="font-medium">{t('marks.completion', 'Completion')}</span>
                  <span className="tabular-nums font-semibold" data-testid="marks-missing-percent">{missData.summary.percent}%</span>
                </div>
                <Progress value={missData.summary.percent} className="h-2" />
                <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground" data-testid="marks-missing-summary">
                  <span>{t('common.total', 'Total')}: <strong className="text-foreground tabular-nums">{missData.summary.total}</strong></span>
                  <span className="text-emerald-600">{t('marks.entered', 'Entered')}: <strong className="tabular-nums">{missData.summary.entered}</strong></span>
                  <span className="text-destructive">{t('marks.missing', 'Missing')}: <strong className="tabular-nums">{missData.summary.missing}</strong></span>
                  {(missData.summary.invalid || 0) > 0 && (
                    <span className="text-destructive" data-testid="marks-missing-invalid-total">{t('marks.overMax', 'Over max')}: <strong className="tabular-nums">{missData.summary.invalid}</strong></span>
                  )}
                </div>
              </div>

              {missData.summary.total === 0 ? (
                <EmptyState icon={Pencil} title={t('common.noData')} description={t('marks.noStudentsClass', 'No students/subjects found for this class.')} />
              ) : (missData.summary.missing === 0 && (missData.summary.invalid || 0) === 0) ? (
                <div className="flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800" data-testid="marks-missing-allgood">
                  <CheckCircle2 className="w-5 h-5 flex-shrink-0" />
                  {t('marks.allEntered', 'All grades have been entered for this class and term.')}
                </div>
              ) : (
                <div className="space-y-5">
                  {missData.summary.missing === 0 && (missData.summary.invalid || 0) > 0 && (
                    <div className="flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="marks-missing-invalid-banner">
                      <AlertTriangle className="w-5 h-5 flex-shrink-0" />
                      {t('marks.allEnteredButOverMax', 'All grades are entered, but some values exceed the subject maximum. Review the highlighted subjects below.')}
                    </div>
                  )}
                  {missData.sections.map((sec) => (
                    <div key={sec.section_id} data-testid={`marks-missing-section-${sec.section_id}`}>
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-sm font-semibold">{t('students.section', 'Section')} {sec.section_name}</h4>
                        <Badge variant="outline" className={sec.missing === 0 ? 'text-emerald-600 border-emerald-200 bg-emerald-50' : 'text-amber-700 border-amber-200 bg-amber-50'}>
                          {sec.entered}/{sec.total}
                        </Badge>
                      </div>
                      <div className="rounded-lg border border-border divide-y divide-border">
                        {sec.subjects.map((subj) => {
                          const key = `${sec.section_id}:${subj.subject_id}`;
                          const status = subj.missing === 0 ? 'complete' : (subj.entered === 0 ? 'empty' : 'partial');
                          const hasInvalid = (subj.invalid || 0) > 0;
                          const expandable = subj.missing > 0 || hasInvalid;
                          return (
                            <div key={key} data-testid={`marks-missing-subject-${subj.subject_id}`}>
                              <div className="flex items-center gap-2 px-3 py-2">
                                <button type="button" className="flex items-center gap-1.5 flex-1 text-left disabled:cursor-default"
                                  onClick={() => expandable && toggleMissRow(key)} disabled={!expandable}>
                                  {expandable ? (missExpanded[key] ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />) : <span className="w-4" />}
                                  <span className="text-sm font-medium">{subj.subject_name}</span>
                                  {subj.uv_group ? <span className="text-xs text-muted-foreground">· {subj.uv_group}</span> : null}
                                </button>
                                {status === 'complete' ? (
                                  <Badge variant="outline" className="text-emerald-600 border-emerald-200 bg-emerald-50 gap-1"><CheckCircle2 className="w-3 h-3" /> {t('marks.complete', 'Complete')}</Badge>
                                ) : (
                                  <Badge variant="outline" className={`gap-1 ${status === 'empty' ? 'text-destructive border-red-200 bg-red-50' : 'text-amber-700 border-amber-200 bg-amber-50'}`}>
                                    <AlertTriangle className="w-3 h-3" /> {subj.missing} {t('marks.missing', 'missing')}
                                  </Badge>
                                )}
                                {hasInvalid && (
                                  <Badge variant="outline" className="gap-1 text-destructive border-red-200 bg-red-50" data-testid={`marks-missing-overmax-${subj.subject_id}`}>
                                    <AlertTriangle className="w-3 h-3" /> {subj.invalid} {t('marks.overMax', 'over max')}
                                  </Badge>
                                )}
                                <span className="text-xs text-muted-foreground tabular-nums w-12 text-right">{subj.entered}/{subj.total}</span>
                                {!missData.is_locked && (
                                  <Button size="sm" variant="ghost" className="h-7 gap-1 text-xs" onClick={() => fillFromMissing(sec.section_id, subj.subject_id)} data-testid={`marks-missing-fill-${subj.subject_id}`}>
                                    {t('marks.fill', 'Fill')} <ArrowRight className="w-3 h-3" />
                                  </Button>
                                )}
                                {SHOW_DIVIDE_TOOLS && !missData.is_locked && subj.entered > 0 && (
                                  <Button size="sm" variant="ghost" className="h-7 gap-1 text-xs text-amber-700 hover:text-amber-800 hover:bg-amber-50"
                                    onClick={() => setScaleTarget({ section_id: sec.section_id, subject_id: subj.subject_id, subject_name: subj.subject_name, count: subj.entered, max_note: subj.max_note })}
                                    data-testid={`marks-divide-${subj.subject_id}`}
                                    title={t('marks.divideHint', 'Divide all C.C & Comp grades for this subject by 2')}>
                                    <Divide className="w-3 h-3" /> ÷2
                                  </Button>
                                )}
                              </div>
                              {expandable && missExpanded[key] && (
                                <div className="px-3 pb-3 pl-9 space-y-2">
                                  {subj.missing > 0 && (
                                    <div className="flex flex-wrap gap-1.5">
                                      {subj.missing_students.map((ms) => {
                                        const mf = ms.missing_fields || [];
                                        const labels = mf.map((f) => f === 'cc' ? t('marks.cc', 'C.C') : t('marks.comp', 'Comp'));
                                        return (
                                          <Badge key={ms.student_id} variant="secondary" className="font-normal" data-testid={`marks-missing-student-${ms.student_id}`}>
                                            {ms.roll_no ? <span className="text-muted-foreground mr-1 tabular-nums">{ms.roll_no}</span> : null}{ms.name}
                                            {labels.length > 0 && (
                                              <span className="ml-1.5 text-[10px] font-medium text-amber-700">({labels.join(', ')})</span>
                                            )}
                                          </Badge>
                                        );
                                      })}
                                    </div>
                                  )}
                                  {hasInvalid && (
                                    <div>
                                      <p className="text-[11px] font-medium text-destructive mb-1">{t('marks.overMaxLabel', 'Grades above maximum')} ({t('subjects.maxNote', 'Max')}: {subj.max_note}):</p>
                                      <div className="flex flex-wrap gap-1.5">
                                        {(subj.invalid_students || []).map((iv) => {
                                          const parts = (iv.invalid_fields || []).map((f) => `${f.component === 'cc' ? t('marks.cc', 'C.C') : t('marks.comp', 'Comp')} ${f.value}/${f.max_note}`);
                                          return (
                                            <Badge key={iv.student_id} variant="outline" className="font-normal text-destructive border-red-200 bg-red-50" data-testid={`marks-overmax-student-${iv.student_id}`}>
                                              {iv.roll_no ? <span className="text-muted-foreground mr-1 tabular-nums">{iv.roll_no}</span> : null}{iv.name}
                                              {parts.length > 0 && <span className="ml-1.5 text-[10px] font-medium">({parts.join(', ')})</span>}
                                            </Badge>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Confirm divide-by-2 */}
      <AlertDialog open={!!scaleTarget} onOpenChange={(o) => { if (!o && !scaling) setScaleTarget(null); }}>
        <AlertDialogContent data-testid="marks-divide-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2"><Divide className="w-5 h-5 text-amber-600" /> {t('marks.divideTitle', 'Divide grades by 2')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('marks.divideConfirm', 'This will divide every C.C and Comp grade by 2 for')}{' '}
              <strong>{scaleTarget?.subject_name}</strong>
              {scaleTarget?.count != null && <> ({scaleTarget.count} {t('marks.gradeRecords', 'grade record(s)')})</>}.{' '}
              {t('marks.divideWarning', 'Absent entries are left unchanged. This cannot be undone automatically.')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={scaling} data-testid="marks-divide-cancel">{t('common.cancel', 'Cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); doScale(); }} disabled={scaling} className="bg-amber-600 hover:bg-amber-700" data-testid="marks-divide-confirm-btn">
              {scaling ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Divide className="w-4 h-4 mr-1" />}
              {scaling ? t('common.saving', 'Saving...') : t('marks.divideApply', 'Divide by 2')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
